import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { stripeService } from '@/services/stripe';
import { buildStripeGooglePayRequest } from '@/utils/google';
import { buildAdyenGooglePayPaymentRequest } from '@/utils/adyen';
import { Errors } from '@/constants/errors';
import type {
  AdyenGooglePayPaymentParams,
  GooglePayPaymentParams,
  StripeExpressCheckoutParams,
} from '@/types/paymentHandlers';
import type { AdyenPaymentResponse, StripePaymentResponse } from '@/types/payment';

// Stripe-specific Google Pay handler
const handleStripeGooglePay = async (
  _checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: StripeExpressCheckoutParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse | null> => {
  const { event, elements } = params;

  const billingDetails = {
    ...event.billingDetails,
    address: {
      ...event.billingDetails?.address,
      line2: event.billingDetails?.address?.line2 || '',
    },
  };

  const googlePayResults = await stripeService.createGooglePayPaymentMethod(elements, billingDetails);
  if (googlePayResults) {
    return (await sendPaymentToServer(
      buildStripeGooglePayRequest(googlePayResults, providerSettings, event.billingDetails!)
    )) as StripePaymentResponse;
  }
  return null;
};

// Adyen-specific Google Pay handler
const handleAdyenGooglePay = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params: AdyenGooglePayPaymentParams,
  providerSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<AdyenPaymentResponse> => {
  const request = buildAdyenGooglePayPaymentRequest(params, providerSettings, checkout.customer);
  return (await sendPaymentToServer(request)) as AdyenPaymentResponse;
};

export const handleGooglePayPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: (params: unknown) => Promise<unknown>,
  params?: GooglePayPaymentParams
): Promise<StripePaymentResponse | AdyenPaymentResponse> => {
  if (!params) {
    throw new Error('Google Pay payment requires parameters');
  }

  const providerSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.GOOGLE_PAY
  );

  if (!providerSettings) {
    throw new Error('Google Pay payment method settings not found');
  }

  try {
    switch (providerSettings.provider) {
      case ProviderEnum.STRIPE:
        return (await handleStripeGooglePay(
          checkout,
          sendPaymentToServer,
          params as StripeExpressCheckoutParams,
          providerSettings
        )) as StripePaymentResponse;
      case ProviderEnum.ADYEN:
        return await handleAdyenGooglePay(
          checkout,
          sendPaymentToServer,
          params as AdyenGooglePayPaymentParams,
          providerSettings
        );
      default:
        throw new Error(`Unsupported provider for Google Pay: ${providerSettings.provider}`);
    }
  } catch (error: unknown) {
    console.error(Errors.google.errorWhileSendingPayment, { cause: error });
    throw error;
  }
};
